import React, { useState } from 'react';
import Dashboard from './components/Dashboard';
import BoothManagement from './components/BoothManagement';
import BeneficiaryMap from './components/BeneficiaryMap';
import SocialListening from './components/SocialListening';
import ContentStudio from './components/ContentStudio';
import APIErrorModal from './components/APIErrorModal';
import { LayoutDashboard, Map, Users, Menu, LogOut, Search, Smartphone, PenTool } from 'lucide-react';

// Using a simple state-based router for a unified experience without URL reload issues
enum Tab {
  DASHBOARD = 'Dashboard',
  BOOTHS = 'Sangathan (Booths)',
  BENEFICIARIES = 'Labharthi (Beneficiaries)',
  SOCIAL = 'Social Media',
  CONTENT = 'Srijan (Content)'
}

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>(Tab.DASHBOARD);
  const [isSidebarOpen, setSidebarOpen] = useState(true);

  const renderContent = () => {
    switch (activeTab) {
      case Tab.DASHBOARD: return <Dashboard />;
      case Tab.BOOTHS: return <BoothManagement />;
      case Tab.BENEFICIARIES: return <BeneficiaryMap />;
      case Tab.SOCIAL: return <SocialListening />;
      case Tab.CONTENT: return <ContentStudio />;
      default: return <Dashboard />;
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-100 font-sans text-slate-900">
      
      {/* Global Error Modal */}
      <APIErrorModal />

      {/* Sidebar */}
      <aside 
        className={`${isSidebarOpen ? 'w-64' : 'w-20'} bg-slate-900 text-white transition-all duration-300 flex flex-col fixed h-full z-20`}
      >
        <div className="h-16 flex items-center justify-center border-b border-slate-700">
           {isSidebarOpen ? (
             <div className="flex flex-col items-center">
               <h1 className="font-bold text-xl tracking-tight text-white">SANKALP <span className="text-bjp-saffron">2026</span></h1>
             </div>
           ) : (
             <span className="text-bjp-saffron font-bold text-xl">S</span>
           )}
        </div>

        <nav className="flex-1 py-6 space-y-2 px-3">
          <button 
            onClick={() => setActiveTab(Tab.DASHBOARD)}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${activeTab === Tab.DASHBOARD ? 'bg-bjp-saffron text-white' : 'text-slate-400 hover:bg-slate-800'}`}
          >
            <LayoutDashboard className="w-5 h-5" />
            {isSidebarOpen && <span>Dashboard</span>}
          </button>

          <button 
            onClick={() => setActiveTab(Tab.BOOTHS)}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${activeTab === Tab.BOOTHS ? 'bg-bjp-saffron text-white' : 'text-slate-400 hover:bg-slate-800'}`}
          >
            <Users className="w-5 h-5" />
            {isSidebarOpen && <span>Sangathan</span>}
          </button>

          <button 
            onClick={() => setActiveTab(Tab.BENEFICIARIES)}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${activeTab === Tab.BENEFICIARIES ? 'bg-bjp-saffron text-white' : 'text-slate-400 hover:bg-slate-800'}`}
          >
            <Map className="w-5 h-5" />
            {isSidebarOpen && <span>Labharthi</span>}
          </button>

          <button 
            onClick={() => setActiveTab(Tab.SOCIAL)}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${activeTab === Tab.SOCIAL ? 'bg-bjp-saffron text-white' : 'text-slate-400 hover:bg-slate-800'}`}
          >
            <Smartphone className="w-5 h-5" />
            {isSidebarOpen && <span>Social Media</span>}
          </button>

          <button 
            onClick={() => setActiveTab(Tab.CONTENT)}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${activeTab === Tab.CONTENT ? 'bg-bjp-saffron text-white' : 'text-slate-400 hover:bg-slate-800'}`}
          >
            <PenTool className="w-5 h-5" />
            {isSidebarOpen && <span>Srijan Studio</span>}
          </button>
        </nav>

        <div className="p-4 border-t border-slate-700">
           <div className={`flex items-center gap-3 ${!isSidebarOpen && 'justify-center'}`}>
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300">
                SD
              </div>
              {isSidebarOpen && (
                <div className="overflow-hidden">
                  <p className="text-sm font-medium text-white truncate">Shashibhusan Deka</p>
                  <p className="text-xs text-slate-500">Candidate</p>
                </div>
              )}
           </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
        
        {/* Header */}
        <header className="h-16 bg-white shadow-sm flex items-center justify-between px-6 sticky top-0 z-10">
           <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(!isSidebarOpen)} className="text-slate-500 hover:text-slate-700">
                 <Menu className="w-6 h-6" />
              </button>
              <div className="hidden md:flex items-center bg-slate-100 rounded-lg px-3 py-1.5 w-64">
                 <Search className="w-4 h-4 text-slate-400 mr-2" />
                 <input type="text" placeholder="Search Voter / Booth..." className="bg-transparent border-none outline-none text-sm w-full" />
              </div>
           </div>
           
           <div className="flex items-center gap-4">
              <div className="text-right hidden md:block">
                 <p className="text-xs font-semibold text-slate-600 uppercase">Constituency</p>
                 <p className="text-sm font-bold text-slate-800">Borkhetri (No. 60)</p>
              </div>
              <button className="p-2 text-slate-400 hover:text-red-500 transition-colors">
                <LogOut className="w-5 h-5" />
              </button>
           </div>
        </header>

        {/* Dynamic Content */}
        <div className="animate-fade-in">
          {renderContent()}
        </div>

      </main>
    </div>
  );
};

export default App;